import { clientLocalDate } from './clientContext'
import type { CoachHealthSummary } from './coachHealthTypes'
import type { TodayProtocolResponse, WorkoutAdjustments } from './dailyOverrideTypes'
import { isRestDayBlock } from './trainingSchedule'
import type { TrainingBlock } from './protocolTypes'

export type ResolvedMorningBrief = {
  text: string | null
  adjustments: WorkoutAdjustments
  displayDate: string
  isScheduledRest: boolean
  isBiometricRest: boolean
  isRestDay: boolean
}

export function weekdayFromLocalIsoDate(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number)
  const noonUtc = new Date(Date.UTC(year, month - 1, day, 12, 0, 0))
  return noonUtc.toLocaleDateString(locale, { weekday: 'long' })
}

function isScheduledRestFromProtocol(protocol: TodayProtocolResponse | null): boolean {
  if (!protocol) return false
  if (protocol.decision_trace?.scheduled_rest_day === true) return true
  const block = protocol.today_block as TrainingBlock | null
  if (block && isRestDayBlock(block)) return true
  return false
}

/** Unified morning brief for Coach UI — today only, with override fallback. */
export function resolveMorningBrief(
  summary: CoachHealthSummary | null,
  todayProtocol: TodayProtocolResponse | null,
  localDate = clientLocalDate(),
): ResolvedMorningBrief {
  const physiologicalDay =
    (summary?.physiological_day as string | undefined) ?? localDate

  const briefing = summary?.todays_briefing
  const briefingDate = briefing?.briefing_date as string | undefined
  const briefingMatchesToday = briefingDate === physiologicalDay || briefingDate === localDate

  const override = todayProtocol?.override
  const overrideMatchesToday = override?.override_date === localDate

  let text: string | null = null
  let adjustments: WorkoutAdjustments = {}

  if (briefingMatchesToday && briefing?.briefing_text) {
    text = String(briefing.briefing_text)
    adjustments = (briefing.adjustments as WorkoutAdjustments) ?? {}
  } else if (overrideMatchesToday && override?.briefing_text) {
    text = override.briefing_text
    adjustments = override.workout_adjustments ?? {}
  }

  const isScheduledRest = isScheduledRestFromProtocol(todayProtocol)
  const isBiometricRest = Boolean(adjustments.rest_day)
  const isRestDay = isScheduledRest || isBiometricRest

  if (isScheduledRest && !isBiometricRest) {
    adjustments = { ...adjustments, rest_day: true }
  }

  return {
    text,
    adjustments,
    displayDate: localDate,
    isScheduledRest,
    isBiometricRest,
    isRestDay,
  }
}
